import type { ShiftStaffingPlan } from 'shared-models';
import { EmployeeLimitWarnings } from '../../../components/EmployeeLimitWarnings';
import { formatDateDot } from '../../../utils/date.utils';
import classes from '../staffing-demand-page.module.scss';

function PlanPagination({ label, offset, total, disabled, onOffset }: {
  label: string; offset: number; total: number; disabled: boolean; onOffset: (value: number) => void;
}) {
  return <nav className={classes.actions} aria-label={label}>
    <button type="button" className="secondaryAction" disabled={disabled || offset === 0} onClick={() => onOffset(Math.max(0, offset - 25))}>הקודם</button>
    <span>{Math.floor(offset / 25) + 1}</span>
    <button type="button" className="secondaryAction" disabled={disabled || offset + 25 >= total} onClick={() => onOffset(offset + 25)}>הבא</button>
  </nav>;
}

export function StaffingPlanPreview({ plan, outdated, disabled, acknowledged, onAcknowledge, onApply,
  assignmentOffset, onAssignmentOffset, unfilledOffset, onUnfilledOffset }: {
  plan: ShiftStaffingPlan; outdated: boolean; disabled: boolean; acknowledged: boolean;
  onAcknowledge: (value: boolean) => void; onApply: () => void;
  assignmentOffset: number; onAssignmentOffset: (value: number) => void; unfilledOffset: number; onUnfilledOffset: (value: number) => void;
}) {
  const visibleAssignments = plan.assignments.slice(assignmentOffset, assignmentOffset + 25);
  const visibleUnfilled = plan.unfilled.slice(unfilledOffset, unfilledOffset + 25);
  return <section aria-label="תצוגת שיבוץ אוטומטי">
    <h2>שיבוץ אוטומטי</h2>
    {outdated && <p role="status">התצוגה אינה עדכנית.</p>}
    <div className={classes.tableWrap}>
      <table className={`reportTable ${classes.table}`}>
        <caption>הצעות שיבוץ: {plan.assignments.length}</caption>
        <thead><tr><th>תאריך</th><th>מחלקה / משמרת / תפקיד</th><th>שעות</th><th>עובד</th></tr></thead>
        <tbody>{visibleAssignments.map(assignment => <tr key={assignment.slotId}>
          <td data-label="תאריך">{formatDateDot(assignment.dateDay)}</td>
          <td data-label="משמרת">{assignment.sectionName}<small>{assignment.shiftName} / {assignment.roleName}</small></td>
          <td data-label="שעות">{assignment.startHour} - {assignment.endHour}</td>
          <td data-label="עובד">{assignment.employeeName}</td>
        </tr>)}</tbody>
      </table>
    </div>
    <PlanPagination label="דפי הצעות שיבוץ" offset={assignmentOffset} total={plan.assignments.length} disabled={disabled} onOffset={onAssignmentOffset} />
    {plan.weeklyLimits.filter(status => visibleAssignments.some(assignment => assignment.employeePk === status.limits.employeePk)).map(status =>
      <div key={status.limits.employeePk}><strong>{status.employeeName}</strong><EmployeeLimitWarnings status={status} /></div>)}
    {plan.unfilled.length > 0 && <>
      <div className={classes.tableWrap}>
        <table className={`reportTable ${classes.table}`}>
          <caption>דרישות שלא מולאו: {plan.unfilled.length}</caption>
          <thead><tr><th>תאריך</th><th>מחלקה / משמרת / תפקיד</th><th>חסרים</th><th>משבצות חסרות</th><th>משבצות ללא מועמד מתאים</th></tr></thead>
          <tbody>{visibleUnfilled.map(cell => <tr key={`${cell.roleId}:${cell.dateDay}`}>
            <td data-label="תאריך">{formatDateDot(cell.dateDay)}</td>
            <td data-label="משמרת">{cell.sectionName}<small>{cell.shiftName} / {cell.roleName}</small></td>
            <td data-label="חסרים">{cell.missingCount}</td><td data-label="משבצות חסרות">{cell.missingSlotCapacity}</td>
            <td data-label="ללא מועמד">{cell.blockedSlotCount}</td>
          </tr>)}</tbody>
        </table>
      </div>
      <PlanPagination label="דפי דרישות שלא מולאו" offset={unfilledOffset} total={plan.unfilled.length} disabled={disabled} onOffset={onUnfilledOffset} />
      <label><input type="checkbox" disabled={disabled || outdated} checked={acknowledged} onChange={event => onAcknowledge(event.currentTarget.checked)} />אני מאשר/ת שיבוץ חלקי</label>
    </>}
    <button type="button" className="primaryAction" disabled={disabled || outdated || !plan.assignments.length || (plan.unfilled.length > 0 && !acknowledged)} onClick={onApply}>החלת שיבוץ</button>
  </section>;
}